/**
 * Mobile sticky gallery pin for bundle visualizer PDPs.
 *
 * Related files (keep in sync):
 * - assets/media-gallery.js — init/destroy lifecycle on <media-gallery>
 * - snippets/product-media-gallery.liquid — markup and data attributes
 * - sections/main-product.liquid — script tags and layout wrappers
 * - tailwind.config.js — styles (mobile breakpoint must match BV_MOBILE_MQ)
 *
 * Load this script before media-gallery.js on bundle PDPs.
 */
const BV_MOBILE_MQ = '(max-width: 767px)';

class BundleStickyGalleryPin {
  static ENTER = 2;
  static EXIT = 24;
  static LOCK_MS = 380;
  static MAX_CROP = 33;
  static MAX_CROP_RATIO = BundleStickyGalleryPin.MAX_CROP / 100;

  constructor(gallery) {
    this.gallery = gallery;
    this.cropFocus = Math.min(100, Math.max(0, Number(gallery.dataset.stickyCropFocus) || 80)) / 100;
    this.topOffset = Math.max(
      0,
      parseFloat(getComputedStyle(gallery).getPropertyValue('--bv-sticky-top-offset')) || 0,
    );
    this.header = null;
    this.isPinned = false;
    this.ticking = false;
    this.lastScrollY = window.scrollY;
    this.lockedUntil = 0;
    this.cropScrollY = null;
    this.metrics = {};
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    };
    this.onResize = () => {
      this.measure();
      this.onScroll();
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.measure();
    this.setPinned(false);
    this.update();
  }

  measure() {
    const pad =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--spacing-sm')) || 15;
    const imageSize = Math.max(0, window.innerWidth - pad * 2);
    const cropRatio = BundleStickyGalleryPin.MAX_CROP_RATIO;
    this.metrics = {
      imageSize,
      full: pad + imageSize,
      min: pad + imageSize * (1 - cropRatio),
      range: imageSize * cropRatio,
    };
  }

  headerBottom() {
    if (!this.header?.isConnected) {
      this.header =
        document.querySelector('header-section.section-header') || document.querySelector('.section-header');
    }
    return this.header ? this.header.getBoundingClientRect().bottom : 60;
  }

  stickyTop() {
    const top = parseFloat(getComputedStyle(this.gallery).top);
    return Number.isFinite(top) ? top : this.headerBottom() + this.topOffset;
  }

  setPinned(next) {
    if (this.isPinned === next) return;
    this.isPinned = next;
    this.gallery.classList.toggle('bv-gallery--pinned', next);
    this.lockedUntil = Date.now() + BundleStickyGalleryPin.LOCK_MS;
    if (!next) this.resetCrop();
  }

  resetCrop() {
    this.cropScrollY = null;
    this.gallery.style.removeProperty('--bv-gallery-height');
    this.gallery.style.removeProperty('--bv-crop-shift');
  }

  updateCrop() {
    if (!this.isPinned) {
      this.resetCrop();
      return;
    }

    const { full, min, range, imageSize } = this.metrics;
    const cropRatio = BundleStickyGalleryPin.MAX_CROP_RATIO;

    if (this.reducedMotion) {
      const visible = imageSize * (1 - cropRatio);
      const windowTop = Math.min(
        Math.max(this.cropFocus * imageSize - visible / 2, 0),
        Math.max(0, imageSize - visible),
      );
      this.gallery.style.setProperty('--bv-gallery-height', `${min}px`);
      this.gallery.style.setProperty('--bv-crop-shift', `${-windowTop}px`);
      return;
    }

    const scrollY = window.scrollY;
    if (this.cropScrollY === null) this.cropScrollY = scrollY;
    if (scrollY < this.cropScrollY) this.cropScrollY = scrollY;

    const progress = Math.min(1, Math.max(0, (scrollY - this.cropScrollY) / range));
    const visible = imageSize * (1 - progress * cropRatio);
    const windowTop = Math.min(Math.max(this.cropFocus * imageSize - visible / 2, 0), Math.max(0, imageSize - visible));

    this.gallery.style.setProperty('--bv-gallery-height', `${full - progress * (full - min)}px`);
    this.gallery.style.setProperty('--bv-crop-shift', `${-windowTop}px`);
  }

  update() {
    const scrollY = window.scrollY;
    if (Date.now() >= this.lockedUntil) {
      const top = this.gallery.getBoundingClientRect().top;
      const stick = this.stickyTop();
      if (this.isPinned) {
        if (scrollY <= 0 || (scrollY < this.lastScrollY && top > stick + BundleStickyGalleryPin.EXIT)) {
          this.setPinned(false);
        }
      } else if (scrollY > 0 && top <= stick + BundleStickyGalleryPin.ENTER) {
        this.setPinned(true);
      }
    }

    this.updateCrop();
    this.lastScrollY = scrollY;
  }

  destroy() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.setPinned(false);
    this.resetCrop();
  }
}

window.BV_MOBILE_MQ = BV_MOBILE_MQ;
window.BundleStickyGalleryPin = BundleStickyGalleryPin;
